import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { PanelModule } from 'primeng/panel';

import { AbsenceService } from '../../../services/absence.service';
import { ActivityService } from '../../../services/activity.service';
import { AuthService } from '../../../services/auth.service';
import { NotificationService } from '../../../services/notification.service';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { Activity } from 'src/app/models/activity.model';
import { DialogManagerService } from 'src/app/services/dialog-manager.service';
import { ABSENCE_TYPES } from '../../../../constants';
import { Absence, AbsencePrefill } from '../../../models/absence.model';
import { Course } from '../../../models/course.model';
import { user } from '../../../models/user.model';
import { CourseService } from '../../../services/course.service';
import { UserService } from '../../../services/user.service';
import { DialogSkeletonComponent } from '../dialog-skeleton/dialog-skeleton.component';
import { FiledropComponent } from '../../filedrop/filedrop.component';
import { DialogResult } from 'src/app/workflow/models/dialog-result.model';
import { toValidDateOr } from '../../../utils/date-helper';

@Component({
  selector: 'app-create-absence-dialog',
  standalone: true,
  imports: [
    DropdownModule,
    DialogModule,
    EditorModule,
    PanelModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CommonModule,
    CalendarModule,
    FloatLabelModule,
    DialogSkeletonComponent,
    FiledropComponent,
  ],
  templateUrl: './create-absence-dialog.component.html',
  styleUrl: './create-absence-dialog.component.scss',
})
export class CreateAbsenceDialogComponent implements OnInit {
  @ViewChild(FiledropComponent) drop!: FiledropComponent;
  courses: Course[] = [];
  participants: user[] = [];
  allParticipants: user[] = [];
  oldFiles: string[] = [];

  usersLoading = false;
  isSubmitting = false;
  activityLoading = false;

  absenceForm: FormGroup;

  absenceTypes = ABSENCE_TYPES;

  selectedUser: user | null = null;
  activityToUpgrade: Activity | undefined;
  absencePrefill: AbsencePrefill | undefined;

  constructor(
    private authService: AuthService,
    private absenceService: AbsenceService,
    private notificationService: NotificationService,
    private activityService: ActivityService,
    private courseService: CourseService,
    private userService: UserService,
    private fb: FormBuilder,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private dialogManager: DialogManagerService,
  ) {}

  ngOnInit(): void {
    this.selectedUser = this.config.data?.selectedUser ?? null;
    this.activityToUpgrade = this.config.data?.activityToUpgrade;
    this.absencePrefill = this.config.data?.absencePrefill;

    this.initializeForm();
    this.loadCourses();
    this.loadParticipants();

    this.absenceForm.get('courseId')?.valueChanges.subscribe((courseId) => {
      this.onCourseSelected(courseId);
    });
  }

  initializeForm(): void {
    // Start-/Enddatum standardmäßig auf heute setzen, damit ohne Vorbelegung
    // kein leerer Wert (und damit z.B. der 01.01.1970) übernommen wird
    const today = new Date();

    this.absenceForm = this.fb.group({
      absenceType: ['Fehlzeit', Validators.required],
      courseId: [null],
      participantId: [null, Validators.required],
      startDate: [today, Validators.required],
      endDate: [today, Validators.required],
    });
  }

  setForm(): void {
    if (this.activityToUpgrade) {
      this.applyActivityToUpgrade(this.activityToUpgrade);
    }

    if (this.selectedUser) {
      this.applySelectedUserDefaults(this.selectedUser);
    }

    if (this.absencePrefill) {
      this.applyAbsencePrefill(this.absencePrefill);
    }
  }

  // Übernimmt Abwesenheitsart, Kurs, Teilnehmer und Datum aus der zu aktualisierenden Aktivität
  private applyActivityToUpgrade(activity: Activity): void {
    this.oldFiles = activity.absence.fileUrls;

    const course = this.courses.find(
      (course) => course.courseName === activity.courseNames?.[0],
    );

    this.absenceForm.setValue({
      absenceType: activity.absence.absenceType,
      courseId: course?.courseId ?? null,
      participantId: activity.users?.[0]?.userId ?? null,
      startDate: toValidDateOr(
        activity.absence.startDate,
        this.absenceForm.get('startDate')?.value,
      ),
      endDate: toValidDateOr(
        activity.absence.endDate,
        this.absenceForm.get('endDate')?.value,
      ),
    });
  }

  // Übernimmt Kurs und Teilnehmer aus dem übergebenen Nutzer
  private applySelectedUserDefaults(selectedUser: user): void {
    this.absenceForm
      .get('courseId')
      ?.setValue(selectedUser.courses?.[0]?.courseId ?? null);
    this.absenceForm.get('participantId')?.setValue(selectedUser.userId);
  }

  // Übernimmt Abwesenheitsart und Zeitraum, z.B. von einer Krankmeldung
  private applyAbsencePrefill(prefill: AbsencePrefill): void {
    this.absenceForm.patchValue({
      absenceType:
        prefill.absenceType ?? this.absenceForm.get('absenceType')?.value,
      startDate: toValidDateOr(
        prefill.startDate,
        this.absenceForm.get('startDate')?.value,
      ),
      endDate: toValidDateOr(
        prefill.endDate,
        this.absenceForm.get('endDate')?.value,
      ),
    });
  }

  onStartDateChanged(date: Date): void {
    this.absenceForm.get('endDate')?.setValue(date);
  }

  onCourseSelected(courseId: number | null): void {
    if (!courseId && courseId !== 0) {
      this.participants = this.allParticipants;
    } else {
      const selectedCourse = this.courses.find((c) => c.courseId === courseId);
      this.participants = selectedCourse
        ? selectedCourse.users.filter((u) => u.roleIds?.includes(1))
        : [];
    }

    const selected = this.absenceForm.get('participantId')?.value;
    const exists = this.participants.some((u) => u.userId === selected);
    if (!exists) {
      this.absenceForm.get('participantId')?.setValue(null);
    }
  }

  loadCourses(): void {
    this.activityLoading = true;
    this.courseService.getCourseObservable().subscribe({
      next: (data) => {
        this.courses = [...data];
        try {
          if (
            this.activityToUpgrade ||
            this.selectedUser ||
            this.absencePrefill
          ) {
            this.setForm();
          }
        } catch (err) {
          console.error(err);
          this.notificationService.error(
            'Formular konnte nicht befüllt werden.',
          );
        } finally {
          this.activityLoading = false;
        }
      },
      error: () => {
        console.error('Kurse nicht geladen!');
        this.activityLoading = false;
      },
    });
  }

  loadParticipants(): void {
    this.usersLoading = true;
    this.userService.getUsersObservable().subscribe((users) => {
      this.allParticipants = users.filter((user) =>
        user.roles.some((role) => role.roleId === 1),
      );
      this.onCourseSelected(this.absenceForm.get('courseId')?.value ?? null);
      this.usersLoading = false;
    });
  }

  closeDialog(result?: DialogResult) {
    this.ref.close(result);
    this.dialogManager.closeAbsenceDialog();
    this.absenceForm.reset();
  }

  createAbsence(formValues: Absence) {
    this.isSubmitting = true;

    this.absenceService.createAbsence(formValues).subscribe({
      next: (activity) => {
        this.closeDialog({ success: true, activityId: activity.activityId });
        this.activityService.addActivity(activity);
        this.notificationService.success(
          `Aktivität "Abwesenheit" erfolgreich erstellt`,
          activity,
        );
        this.isSubmitting = false;
      },
      error: (error: HttpErrorResponse) => {
        this.handleAbsenceSaveError(
          error,
          'Aktivitat "Abwesenheit" NICHT erstellt',
        );
        this.isSubmitting = false;
      },
    });
  }

  updateAbsence(absence: Absence) {
    absence.absenceId = this.activityToUpgrade?.absence?.absenceId;
    absence.activityId = this.activityToUpgrade?.activityId;
    this.isSubmitting = true;

    this.absenceService.updateAbsence(absence).subscribe({
      next: (activity) => {
        this.closeDialog({ success: true });
        this.activityService.updateActivity(activity);
        this.notificationService.success(
          `Aktivität "Abwesenheit" erfolgreich aktualisiert`,
          activity,
        );
        this.isSubmitting = false;
      },
      error: (error: HttpErrorResponse) => {
        this.handleAbsenceSaveError(
          error,
          'Aktivitat "Abwesenheit" NICHT aktualisiert',
        );
        this.isSubmitting = false;
      },
    });
  }

  // Zeigt bei einem 409-Konflikt (z.B. überschneidende Abwesenheit) die konkrete Backend-Meldung an, sonst die generische Fehlermeldung
  private handleAbsenceSaveError(
    error: HttpErrorResponse,
    fallbackMessage: string,
  ): void {
    if (error.status === 409) {
      const message =
        typeof error.error === 'string' ? error.error : error.error?.message;
      this.notificationService.error(
        message ??
          'Für diesen Teilnehmer besteht bereits eine Abwesenheit im gewählten Zeitraum.',
      );
      return;
    }
    this.notificationService.error(fallbackMessage);
  }

  absenceDialogSubmit(): void {
    this.isSubmitting = true;
    this.drop.requestUpload();
  }

  onUploadStart(): void {
    this.isSubmitting = true;
  }

  onUploadDone(urls: string[]): void {
    if (!this.isSubmitting) return;
    this.isSubmitting = false;

    const rawForm = this.absenceForm.getRawValue();
    const courseId = rawForm.courseId;
    const participantId = rawForm.participantId;

    const startDate = new Date(rawForm.startDate);
    startDate.setHours(startDate.getHours() + 12);

    const endDate = new Date(rawForm.endDate);
    endDate.setHours(endDate.getHours() + 12);

    const formValues: Absence = {
      creatorId: this.authService.getCurrentUserId(),
      absenceType: this.absenceForm.value.absenceType,
      selectedCourseIds: courseId == null ? [] : [courseId],
      selectedParticipantIds: participantId == null ? [] : [participantId],
      startDate: startDate,
      endDate: endDate,
      fileUrls: urls,
      // Nur beim Erstellen aus einer Krankmeldung gesetzt. Beim Bearbeiten ist
      // absencePrefill leer -> undefined wird von JSON.stringify verworfen, der
      // Schlüssel fehlt dann im Body und das Backend lässt den gespeicherten
      // Inhalt unangetastet.
      emailContent: this.absencePrefill?.emailContent,
    };

    if (this.activityToUpgrade) {
      this.updateAbsence(formValues);
    } else {
      this.createAbsence(formValues);
    }
  }

  onUserSelected(event: DropdownChangeEvent): void {
    const userId = event.value;
    if (userId == null) {
      return;
    }

    const selectedUser =
      this.allParticipants.find((u) => u.userId === userId) ??
      this.participants.find((u) => u.userId === userId);
    if (!selectedUser) {
      return;
    }

    this.applySelectedUserDefaults(selectedUser);
  }
}
